import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Save, ArrowLeft, Shield, Lock, Edit3, PlusSquare, Trash2, 
  Package, ShoppingCart, Layout, Wrench, Users, MapPin, Gauge, Building2, Menu, Network, Workflow, BarChart, AlertCircle, UserCheck
} from 'lucide-react';

const MODULE_GROUPS = [
  {
    group: "Core Security",
    modules: [
      { id: 'users', label: 'Users & Security', icon: Lock },
      { id: 'roles', label: 'Roles Management', icon: Shield },
      { id: 'dashboard', label: 'Dashboard', icon: BarChart },
    ]
  },
  {
    group: "Inventory & Supply Chain",
    modules: [
      { id: 'items', label: 'Item Master', icon: Package },
      { id: 'tools', label: 'Tools', icon: Package },
      { id: 'inventory', label: 'Inventory (Balances)', icon: Layout },
      { id: 'invusage', label: 'Inventory Usage', icon: Layout },
      { id: 'storerooms', label: 'Storerooms', icon: MapPin },
      { id: 'receiving', label: 'Receiving', icon: ShoppingCart },
    ]
  },
  {
    group: "Purchasing",
    modules: [
      { id: 'pr', label: 'Purchase Requisitions (PR)', icon: ShoppingCart },
      { id: 'po', label: 'Purchase Orders (PO)', icon: ShoppingCart },
      { id: 'rfq', label: 'Request fro Quotation (RFQ)', icon: ShoppingCart },
      { id: 'mr', label: 'Material Requests (MR)', icon: ShoppingCart },
      { id: 'vendors', label: 'Vendors', icon: UserCheck },
    ]
  },
  {
    group: "Work & Assets",
    modules: [
      { id: 'assets', label: 'Assets', icon: Shield },
      { id: 'locations', label: 'Locations', icon: MapPin },
      { id: 'workorders', label: 'Work Orders', icon: Edit3 },
      { id: 'pm', label: 'Preventive Maintenance', icon: Wrench },
      { id: 'jp', label: 'Job Plans', icon: Wrench },
      { id: 'meters', label: 'Meters', icon: Gauge },
      { id: 'asset-categories', label: 'Asset Category', icon: Layout},
      { id: 'failure-codes', label: 'Failure Code', icon: Shield},
    ]
  },
  {
    group: "Service Requests",
    modules: [
      { id: 'sr', label: 'Service Requests', icon: ShoppingCart },
    ]
  },  
  {
    group: "Incidents",
    modules: [
      { id: 'incidents', label: 'Incident', icon: AlertCircle },
    ]
  },   
  {
    group: "Resources",
    modules: [
      { id: 'labor', label: 'Labor', icon: Users },
      { id: 'craft', label: 'Crafts', icon: Wrench },
      { id: 'person', label: 'Person', icon: UserCheck },
    ]
  },
  {
    group: "Administration",
    modules: [
      { id: 'organizations', label: 'Organizations', icon: Building2 },
      { id: 'domains', label: 'Domains', icon: Menu },
      { id: 'classifications', label: 'Classifications', icon: Network },
      { id: 'cobiemanager', label: 'Cobie Manager', icon: Layout },
      { id: 'workflow', label: 'Workflow', icon: Workflow },
      { id: 'uniimporter', label: 'Universal Import', icon: PlusSquare },
      { id: 'admin/workflow/studio', label: 'Action Script', icon: Workflow },
    ]
  }  
];

const generateDefaultPermissions = () => {
  const perms = {};
  MODULE_GROUPS.forEach(group => {
    group.modules.forEach(mod => {
      perms[mod.id] = { read: false, create: false, update: false, delete: false };
    });
  });
  return perms;
};

const DEFAULT_PERMISSIONS = generateDefaultPermissions();

const RoleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new' || id === 'undefined';
  const [loading, setLoading] = useState(false);
  const [roleData, setRoleData] = useState({ name: '', permissions: DEFAULT_PERMISSIONS });

  useEffect(() => {
    const fetchRole = async () => {
      if (isNew) return;
      setLoading(true);
      try {
        const res = await axios.get(`/api/roles/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        // تأكد من معالجة الصلاحيات سواء كانت String أو Object
        let perms = typeof res.data.permissions === 'string' ? JSON.parse(res.data.permissions) : res.data.permissions;
        setRoleData({ 
            id: res.data.id,
            name: res.data.name || res.data.role_name, 
            permissions: { ...DEFAULT_PERMISSIONS, ...perms } 
        });
      } catch (err) { 
        toast.error("Error loading role details"); 
      } finally { 
        setLoading(false); 
      }
    };
    fetchRole();
  }, [id, isNew]);

  const handlePermissionChange = (modId, action) => {
    setRoleData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [modId]: { ...prev.permissions[modId], [action]: !prev.permissions[modId][action] }
      }
    }));
  };

  // دالة الـ Select All للمجموعة
  const toggleGroupAction = (modules, action) => {
    const allActive = modules.every(m => roleData.permissions[m.id][action]);
    const updatedPerms = { ...roleData.permissions };
    modules.forEach(m => {
      updatedPerms[m.id][action] = !allActive;
    });
    setRoleData({ ...roleData, permissions: updatedPerms });
  };

  const handleSave = async () => {
    if (!roleData.name) return toast.error("Role Name is required");
    setLoading(true);
    try {
      // نرسل الصلاحيات كـ JSON string للباكيند
      const payload = { 
        name: roleData.name, 
        permissions: JSON.stringify(roleData.permissions) 
      };
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      
      if (isNew) {
        await axios.post('/api/roles', payload, { headers });
      } else {
        await axios.put(`/api/roles/${id}`, payload, { headers });
      }
      
      toast.success("Security Role Saved Successfully");
      navigate('/roles');
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Save failed"); 
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6] font-bold uppercase tracking-tighter text-[11px]">
      {/* Header Toolbar */}
      <div className="bg-white p-3 border-b shadow-sm flex justify-between items-center sticky top-0 z-20">
        <div className="flex items-center gap-4 ml-4">
          <button onClick={() => navigate('/roles')} className="p-1 hover:bg-gray-100 rounded-full text-blue-900 transition-colors">
            <ArrowLeft size={18}/>
          </button>
          <div className="flex flex-col">
            <span className="text-[9px] text-gray-400 italic font-black">Security Role Config</span>
            <span className="text-blue-900 font-black text-lg leading-tight">
                {roleData.name || 'NEW ENTERPRISE ROLE'}
            </span>
          </div>
        </div>
        <button 
          onClick={handleSave} 
          disabled={loading} 
          className="bg-[#005495] text-white px-10 py-2.5 rounded-sm flex items-center gap-2 hover:bg-black transition-all shadow-lg active:scale-95 disabled:opacity-50"
        >
          <Save size={16} /> {loading ? 'PROCESSING...' : 'SAVE ROLE'}
        </button>
      </div>

      <div className="flex-1 overflow-auto p-8 max-w-6xl mx-auto w-full space-y-8">
        {/* Role Name Input */}
        <div className="bg-white border-2 border-gray-200 p-6 rounded-sm shadow-sm">
          <label className="text-[9px] text-gray-400 block mb-1 tracking-widest">ROLE IDENTIFIER</label>
          <input 
            className="w-full border-b-2 border-blue-900 outline-none p-2 text-xl font-black text-blue-900 uppercase placeholder-gray-200" 
            placeholder="E.G. SR_CLERK" 
            value={roleData.name} 
            onChange={(e) => setRoleData({...roleData, name: e.target.value.toUpperCase()})} 
          />
        </div>

        {/* Permissions Groups */}
        {MODULE_GROUPS.map((group, gIdx) => (
          <div key={gIdx} className="bg-white border-2 border-gray-200 rounded-sm shadow-sm overflow-hidden">
            <div className="bg-[#1a3353] text-white p-3 text-[10px] tracking-widest flex items-center justify-between font-black border-b">
              <span className="flex items-center gap-2 text-blue-300">
                <Shield size={14} className="text-orange-400"/> {group.group}
              </span>
              <div className="flex gap-4 items-center">
                  <span className="text-[8px] text-gray-400 mr-2 italic">QUICK TOGGLE:</span>
                  {['read', 'create', 'update', 'delete'].map(action => (
                    <button 
                      key={action}
                      onClick={() => toggleGroupAction(group.modules, action)}
                      className="hover:text-orange-400 transition-colors cursor-pointer border border-white/20 px-1.5 py-0.5 rounded uppercase text-[8px]"
                    >
                      All {action}
                    </button>
                  ))}
              </div>
            </div>
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-[9px] text-gray-400 border-b">
                <tr>
                  <th className="p-4 w-2/5 tracking-widest">Application / Module</th>
                  {['READ', 'CREATE', 'UPDATE', 'DELETE'].map(a => (
                    <th key={a} className="p-4 text-center border-l w-20 tracking-widest">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {group.modules.map((mod) => (
                  <tr key={mod.id} className="hover:bg-blue-50/30 group transition-colors">
                    <td className="p-4 flex items-center gap-3 font-black text-blue-900">
                      <mod.icon size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                      {mod.label}
                    </td>
                    {['read', 'create', 'update', 'delete'].map(action => (
                      <td key={action} className="p-4 text-center border-l border-gray-50">
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 accent-blue-900 cursor-pointer shadow-sm" 
                          checked={roleData.permissions?.[mod.id]?.[action] || false} 
                          onChange={() => handlePermissionChange(mod.id, action)} 
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoleDetails;